// Every string PurchaseCard renders of its own, and the shape a caller may
// replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The card's own words are the one after the price, the MMR label and the
// action labels. Everything else it draws — the asset's title, the sale and the
// city, the hint beside the chip — arrives from the page already in the
// reader's language.
//
// The chip's two labels are PurchaseStatusChip's. `status` hands a translation
// of them through to the chip, and the defaults below are the chip's own object
// rather than a second copy of its words, so the walk can check a translation's
// keys without this file owning them.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';
import { PURCHASE_STATUS_CHIP_TRANSLATIONS } from './purchaseStatusChipTranslations';

/**
 * The English defaults, frozen.
 *
 * `priceLabel` is keyed by outcome, as the chip's labels are: the My Purchases
 * screen prints "sold" after the price of an asset the buyer bought, and "your
 * offer" after the buyer's bid on an if-sale, which has not sold. The action
 * labels are the screen's, and "MMR" is the label before the asset's MMR.
 */
export const PURCHASE_CARD_TRANSLATIONS = Object.freeze({
  priceLabel: Object.freeze({
    won: 'sold',
    if: 'your offer',
  }),
  mmr: 'MMR',
  checkout: 'Checkout',
  invoice: 'Invoice',
  arbitration: 'Arbitration',
  viewOffer: 'View offer',
  status: PURCHASE_STATUS_CHIP_TRANSLATIONS,
});

/**
 * The keys in `translations` that PurchaseCard would not read, as dotted paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument
 * and descends into the nested ones, so `priceLabel` and `status` are checked
 * key by key like everything else.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, PURCHASE_CARD_TRANSLATIONS);

/**
 * One language's text over the English defaults, key by key.
 *
 * `status` is not merged here: the card passes the page's translation of it
 * straight to the chip, which merges it over its own defaults.
 */
export function mergeTranslations(translations) {
  if (!translations) return PURCHASE_CARD_TRANSLATIONS;

  return {
    ...PURCHASE_CARD_TRANSLATIONS,
    ...translations,
    priceLabel: { ...PURCHASE_CARD_TRANSLATIONS.priceLabel, ...(translations.priceLabel ?? {}) },
  };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidPurchaseCardI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
